import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import { MagicCard } from "@/components/ui/magic-card";
import { useTheme } from "next-themes";
import { MagnetizeButton } from "@/components/ui/magnetize-button";
import { Pencil, Puzzle, BookOpen } from "lucide-react";
import { Typewriter } from "@/components/ui/typewriter";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMemo } from "react";
import { RainbowButton } from "@/components/ui/rainbow-button";

const HomePage = () => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const gradientColor = theme === "dark" ? "#3a3a3a" : "#e2d1c380";

  const topics = useMemo(() => {
    const topics = [
      "Taylor Swift Songs 🎤",
      "Pasta Shapes 🍝",
      "NBA 🏀 & NFL 🏈",
      "90s Rom Com Movies 🎥",
      "Dog Breeds 🐶",
      "Hip-Hop 🎵",
      "Harry Potter ⚡"
    ];
    // Fisher-Yates shuffle algorithm
    for (let i = topics.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [topics[i], topics[j]] = [topics[j], topics[i]];
    }
    return topics;
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-6 sm:py-12">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className={`${isMobile ? 'text-5xl' : 'text-4xl sm:text-5xl md:text-6xl'} font-bold tracking-tight bg-gradient-to-r from-[#a3b18a] to-primary bg-clip-text text-transparent`}>
            <span>Create Custom Crosswords About {isMobile && <br />}</span>
            <div className={`${isMobile ? 'h-28' : ''}`}>
              <Typewriter
                text={topics}
                speed={70}
                waitTime={2000}
                deleteSpeed={40}
                className="text-[#bc6c25]"
              />
            </div>
          </h1>
          
          <p className="mt-4 sm:mt-8 text-base sm:text-xl text-muted-foreground">
          Play crosswords made for you. Learn as you go. Share the fun.
          </p>
          
          <div className={`mt-6 sm:mt-10 grid gap-3 sm:gap-6 ${isMobile ? 'grid-cols-1' : 'sm:grid-cols-2'} max-w-lg mx-auto`}>
            <RainbowButton className="w-full max-w-sm py-3 rounded-xl shadow mt-2 hover:from-purple-400 hover:to-purple-700 transition-all relative overflow-hidden">
              <Link to="/create" className="w-full h-full flex items-center justify-center whitespace-nowrap">
                Create Crossword
              </Link>
            </RainbowButton>
            
            <RainbowButton variant="dark" className="w-full max-w-sm py-3 rounded-xl shadow mt-2 hover:from-purple-400 hover:to-purple-700 transition-all relative overflow-hidden">
              <Link to="/explore" className="w-full h-full flex items-center justify-center">
                Explore Puzzles
              </Link>
            </RainbowButton>
          </div>
        </div>
        
        <div className="mt-8 sm:mt-16 max-w-4xl mx-auto" id="how-it-works">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-16">How It Works</h2>
          
          <div className={`grid gap-4 sm:gap-8 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-3'} h-auto`}>
            <MagicCard 
              className="flex flex-col items-center justify-center p-4 sm:p-8 cursor-pointer transition-all hover:shadow-xl shadow-md bg-opacity-75 backdrop-blur-sm text-center"
              gradientColor={gradientColor}
              gradientOpacity={0.6}
            >
              <div className="relative w-14 h-14 sm:w-16 sm:h-16 mb-4 sm:mb-5 mx-auto">
                <div className="absolute inset-0 rounded-full bg-primary/10 flex items-center justify-center shadow-inner">
                  <Pencil className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
                </div>
                <span className="absolute -right-2 -bottom-2 w-7 h-7 sm:w-8 sm:h-8 text-white bg-[#2d5a27] p-1 rounded-full shadow-sm flex items-center justify-center text-base sm:text-lg font-bold">1</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">Share Your Interests</h3>
              <p className="text-sm sm:text-base text-muted-foreground">
                Tell us about topics you love and we'll use them to create your puzzle.
              </p>
            </MagicCard>
            
            <MagicCard 
              className="flex flex-col items-center justify-center p-4 sm:p-8 cursor-pointer transition-all hover:shadow-xl shadow-md bg-opacity-75 backdrop-blur-sm text-center"
              gradientColor={gradientColor}
              gradientOpacity={0.6}
            >
              <div className="relative w-14 h-14 sm:w-16 sm:h-16 mb-4 sm:mb-5 mx-auto">
                <div className="absolute inset-0 rounded-full bg-primary/10 flex items-center justify-center shadow-inner">
                  <Puzzle className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
                </div>
                <span className="absolute -right-2 -bottom-2 w-7 h-7 sm:w-8 sm:h-8 text-white bg-[#2d5a27] p-1 rounded-full shadow-sm flex items-center justify-center text-base sm:text-lg font-bold">2</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">Easy Generation</h3>
              <p className="text-sm sm:text-base text-muted-foreground">
                Our algorithm creates a unique crossword puzzle with clues related to your topics.
              </p>
            </MagicCard>
            
            <MagicCard 
              className="flex flex-col items-center justify-center p-4 sm:p-8 cursor-pointer transition-all hover:shadow-xl shadow-md bg-opacity-75 backdrop-blur-sm text-center"
              gradientColor={gradientColor}
              gradientOpacity={0.6}
            >
              <div className="relative w-14 h-14 sm:w-16 sm:h-16 mb-4 sm:mb-5 mx-auto">
                <div className="absolute inset-0 rounded-full bg-primary/10 flex items-center justify-center shadow-inner">
                  <BookOpen className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
                </div>
                <span className="absolute -right-2 -bottom-2 w-7 h-7 sm:w-8 sm:h-8 text-white bg-[#2d5a27] p-1 rounded-full shadow-sm flex items-center justify-center text-base sm:text-lg font-bold">3</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">Learn While Playing</h3>
              <p className="text-sm sm:text-base text-muted-foreground">
                Reinforce your knowledge in fun ways with puzzles that match your learning goals.
              </p>
            </MagicCard>
          </div>
        </div>
      </main>
      
      <footer className="mt-auto py-2 sm:py-3 bg-secondary/80" style={{ color: '#283618' }}>
        <div className="container mx-auto px-4 text-center text-xs sm:text-sm text-primary">
          <p>© 2025 Crossable. All rights reserved.</p>
          <p className="mt-2">Business inquiries, feedback, or bug reports? Reach us at crossabledev@gmail.com</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
